import React, {
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { DateTime } from 'luxon';
import { getDateRange } from '../../../../../../shared/components/date-filter/date-filter-options';
import CallLogsHeader from '../call-logs-header/call-logs-header';
import ReportsSubHeader from './components/reports-sub-header';
import ReportsSummaryCards from './components/reports-summary-cards';
import ReportsIncomingTable from './components/reports-incoming-table';
import {
  mapTeamDataToAccordionRow,
  mapUserCallDataToRow,
  mapReportsStatsToSummaryItems,
} from './utils/reports-utils';
import { exportCallLogsDetailed } from '../../../../helpers/dialer.api';
import './reports.scss';
import {
  CallLogsListCountsResponse,
  DefaultSortByField,
  ReportTableMode,
  ReportsPayload,
  TeamData,
  UserCallData,
} from '../../../../types';
import {
  DialerReportType,
  DialerSortFields,
  DialerSortOrder,
} from '../../../../enums/prospect';
import { SortOrder } from '../../../../../../shared/enums/order';
import type {
  CallDirectionFilter,
  ReportFilters,
} from './components/reports-sub-header';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import type { DialerReportSummaryItem } from './types';
import { TeamsAndUsersList } from '../../../../../settings/types/users-and-teams';
import { callerHostOptions } from '../../utils/helpers';
import toaster from '../../../../../../shared/toaster';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import { RequestState } from '../../../../../../shared/types/request-state';

interface IProps {
  onMakeCallClick?: (show: boolean) => void;
  showDialer?: boolean;
  callLogsListCountsResponse?: CallLogsListCountsResponse;
  reportTableMode?: ReportTableMode;
  getReportTeamListResponse?: {
    data?: TeamData[];
    payload?: { data?: TeamData[]; total?: string | number };
    total?: string | number;
  } | null;
  getReportIndividualListResponse?: {
    data?: UserCallData[];
    payload?: { data?: UserCallData[]; total?: string | number };
    total?: string | number;
  } | null;
  getReportTeamListRequestStatus?: string;
  getReportIndividualListRequestStatus?: string;
  getReportsStatsResponse?: {
    payload?: Record<
      string,
      { value: number; trend: number; percentage?: string }
    >;
  } | null;
  getReportsStatsRequestStatus?: string;
  getReportTeamListRequest?: (payload: ReportsPayload) => any;
  getReportIndividualListRequest?: (payload: ReportsPayload) => any;
  getReportsStatsRequest?: (payload: ReportsPayload) => any;
  teamMemeberListResponse?: TeamsAndUsersList[];
  sendGetTeamsAndMembersListRequest?: () => void;
  exportCallReportsRequest?: (payload: ReportsPayload) => any;
  exportCallReportsRequestStatus?: string;
  exportCallReportsResponse?: {
    message: string;
    status: number;
  };
  userRole?: string;
  resetGetReportTeamListRequest?: () => void;
  resetGetReportIndividualListRequest?: () => void;
  resetGetReportsStatsRequest?: () => void;
  resetExportCallReportsRequest?: () => void;
  assignCallOutcomeRequestStatus?: RequestState;
  resetAssignCallOutcomeRequest?: () => void;
  createCallLogsNoteRequestStatus?: RequestStatus;
  resetCreateCallLogsNoteRequest?: () => void;
}

const getTeamListData = (
  response: IProps['getReportTeamListResponse'],
): TeamData[] => {
  if (!response) return [];
  return response.payload?.data ?? response.data ?? [];
};

const getIndividualListData = (
  response: IProps['getReportIndividualListResponse'],
): UserCallData[] => {
  if (!response) return [];
  return response.payload?.data ?? response.data ?? [];
};

const getReportListTotal = (
  response:
    | IProps['getReportTeamListResponse']
    | IProps['getReportIndividualListResponse']
    | null
    | undefined,
): number => {
  if (!response) return 0;
  const raw =
    response.payload?.total !== undefined
      ? response.payload.total
      : response.total;
  if (raw === undefined || raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

type FetchReportsOverrides = Partial<{
  callDirection: CallDirectionFilter;
  userIds: number[];
  startDate: string;
  endDate: string;
  sortBy: DialerSortFields;
  sortOrder: DialerSortOrder;
  pageNumber: number;
  pageSize: number;
}>;

/** Maps table column id to API DialerSortFields */
const COLUMN_TO_SORT_FIELD: Record<string, DialerSortFields> = {
  totalCallsReceived: DialerSortFields.TotalCallsReceived,
  callsForwarded: DialerSortFields.ForwardedCallCount,
  callsPicked: DialerSortFields.CallsPicked,
  callsUnanswered: DialerSortFields.CallsUnanswered,
  totalTalkTimeMins: DialerSortFields.TotalTalkTime,
  avgTalkTimeMins: DialerSortFields.AvgTalkTime,
  totalCallsMade: DialerSortFields.TotalCallsMade,
  callsConnected: DialerSortFields.CallsConnected,
  avgRingDurationSecs: DialerSortFields.AvgRingDuration,
};

/**
 * Reports tab: Outgoing (KPI cards + table) vs Incoming (table only).
 * Table type (accordion vs simple) is controlled by reportTableMode prop.
 * Fetches data from dialer reports API.
 */
const Reports: React.FC<IProps> = ({
  onMakeCallClick = null,
  showDialer,
  callLogsListCountsResponse,
  userRole,
  getReportTeamListResponse,
  getReportIndividualListResponse,
  getReportTeamListRequestStatus,
  getReportIndividualListRequestStatus,
  getReportsStatsResponse,
  getReportsStatsRequestStatus,
  getReportTeamListRequest,
  getReportIndividualListRequest,
  getReportsStatsRequest,
  teamMemeberListResponse,
  exportCallReportsRequest,
  exportCallReportsResponse,
  resetGetReportTeamListRequest,
  resetGetReportIndividualListRequest,
  resetGetReportsStatsRequest,
  resetExportCallReportsRequest,
  resetExportCallLogsDetailedRequest,
  exportCallReportsRequestStatus,
  assignCallOutcomeRequestStatus,
  resetAssignCallOutcomeRequest,
  createCallLogsNoteRequestStatus,
  resetCreateCallLogsNoteRequest,
}) => {
  const reportTableMode = ['admin', 'owner', 'demo-client-user']?.includes(userRole)
    ? 'team'
    : 'individual';
  const [callDirection, setCallDirection] = useState<CallDirectionFilter>(
    'outgoing',
  );
  const defaultRange = getDateRange('last_30_days', DateTime.local().zoneName);
  const [reportFilters, setReportFilters] = useState<ReportFilters>({
    userIds: [],
    startDate: defaultRange.startDate.toISO(),
    endDate: defaultRange.endDate.toISO(),
  });

  const [sortBy, setSortBy] = useState<DialerSortFields>(
    DefaultSortByField[callDirection as DialerReportType],
  );
  const [sortOrder, setSortOrder] = useState<DialerSortOrder>(
    DialerSortOrder.Desc,
  );

  const [reportPagination, setReportPagination] = useState({
    page: 1,
    limit: 25,
  });

  const [isExporting, setIsExporting] = useState(false);
  const [isDetailedExporting, setIsDetailedExporting] = useState(false);

  const fetchReportsData = useCallback(
    (overrides?: FetchReportsOverrides) => {
      const direction = overrides?.callDirection ?? callDirection;
      const reportType =
        direction === 'incoming'
          ? DialerReportType.Incoming
          : DialerReportType.Outgoing;
      const userIds = overrides?.userIds ?? reportFilters.userIds;
      if (!userIds?.length) return;

      const startDate = overrides?.startDate ?? reportFilters.startDate;
      const endDate = overrides?.endDate ?? reportFilters.endDate;
      const sb = overrides?.sortBy ?? sortBy;
      const so = overrides?.sortOrder ?? sortOrder;
      const pageNum = overrides?.pageNumber ?? reportPagination.page;
      const pageSz = overrides?.pageSize ?? reportPagination.limit;

      getReportsStatsRequest?.({
        reportType,
        userIds,
        startDate,
        endDate,
      });

      const listPayload: ReportsPayload = {
        reportType,
        userIds,
        startDate,
        endDate,
        sortBy: sb,
        sortOrder: so,
        pageNumber: pageNum,
        pageSize: pageSz,
      };

      if (reportTableMode === 'team') {
        getReportTeamListRequest?.(listPayload);
      } else {
        getReportIndividualListRequest?.(listPayload);
      }
    },
    [
      callDirection,
      reportFilters,
      sortBy,
      sortOrder,
      reportPagination.page,
      reportPagination.limit,
      reportTableMode,
      getReportsStatsRequest,
      getReportTeamListRequest,
      getReportIndividualListRequest,
    ],
  );

  const fetchReportsDataRef = useRef(fetchReportsData);
  fetchReportsDataRef.current = fetchReportsData;

  const isListLoading =
    reportTableMode === 'team'
      ? getReportTeamListRequestStatus === RequestStatus.Pending ||
        teamMemeberListResponse?.length === 0
      : getReportIndividualListRequestStatus === RequestStatus.Pending ||
        teamMemeberListResponse?.length === 0;

  const isStatsLoading =
    getReportsStatsRequestStatus === RequestStatus.Pending ||
    teamMemeberListResponse?.length === 0;

  const handleSort = useCallback(
    (headerName: string, order: SortOrder) => {
      const apiSortBy =
        COLUMN_TO_SORT_FIELD[headerName] ?? (headerName as DialerSortFields);
      const nextOrder = (order as unknown) as DialerSortOrder;
      setSortBy(apiSortBy);
      setSortOrder(nextOrder);
      setReportPagination((prev) => ({
        ...prev,
        page: reportPagination?.page,
      }));
      fetchReportsData({
        sortBy: apiSortBy,
        sortOrder: nextOrder,
        pageNumber: reportPagination?.page,
        pageSize: reportPagination?.limit,
        userIds: reportFilters?.userIds,
        startDate: reportFilters?.startDate,
        endDate: reportFilters?.endDate,
      });
    },
    [fetchReportsData, reportPagination, reportFilters],
  );

  const handleFiltersChange = useCallback(
    (filters: ReportFilters) => {
      setReportFilters(filters);
      setReportPagination((prev) => ({ ...prev, page: 1 }));
      fetchReportsData({
        userIds: filters.userIds,
        startDate: filters.startDate,
        endDate: filters.endDate,
        pageNumber: 1,
        pageSize: reportPagination?.limit,
      });
    },
    [fetchReportsData, reportPagination],
  );

  const callerHostOptionsListIds = useMemo(
    () =>
      callerHostOptions(teamMemeberListResponse)
        ?.map((o) => o?.id)
        ?.filter((n) => !Number.isNaN(n)) ?? [],
    [teamMemeberListResponse],
  );

  const callerHostOptionsIdsKey = useMemo(
    () =>
      callerHostOptionsListIds
        .map((id) => Number(id))
        .sort((a, b) => a - b)
        .join(','),
    [callerHostOptionsListIds],
  );

  useEffect(() => {
    if (callerHostOptionsListIds.length === 0) return;
    const nextUserIds = callerHostOptionsListIds.map((id) => Number(id));
    setReportFilters((prev) => ({ ...prev, userIds: nextUserIds }));
    setReportPagination((prev) => ({ ...prev, page: 1 }));
    fetchReportsDataRef.current({
      userIds: nextUserIds,
      pageNumber: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- host id set is keyed only; fetch via ref
  }, [callerHostOptionsIdsKey]);

  const handleExport = useCallback(async () => {
    if (isExporting) return;
    if (reportFilters?.userIds?.length === 0) {
      toaster.error('Please select at least one team member to export');
      return;
    }

    setIsExporting(true);
    const payload: ReportsPayload = {
      exportType: reportTableMode,
      reportType:
        callDirection === 'incoming'
          ? DialerReportType.Incoming
          : DialerReportType.Outgoing,
      userIds: reportFilters?.userIds || [],
      startDate: reportFilters?.startDate,
      endDate: reportFilters?.endDate,
    };

    try {
      await exportCallReportsRequest?.(payload);
      toaster.success(
        exportCallReportsResponse?.message || 'Export started successfully',
      );
    } catch (e: any) {
      toaster.error(e?.payload?.message || e?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    callDirection,
    reportFilters,
    sortBy,
    sortOrder,
    reportTableMode,
    exportCallReportsRequest,
    exportCallReportsResponse,
  ]);

  const handleDetailedExport = useCallback(async () => {
    if (isDetailedExporting) return;
    if (reportFilters?.userIds?.length === 0) {
      toaster.error('Please select at least one team member to export');
      return;
    }

    setIsDetailedExporting(true);
    try {
      const result = await exportCallLogsDetailed({
        userIds: reportFilters?.userIds || [],
        startDate: reportFilters?.startDate,
        endDate: reportFilters?.endDate,
      });
      toaster.success(
        result?.payload?.message ||
          result?.message ||
          'Export started successfully',
      );
    } catch (e: any) {
      toaster.error(e?.message || 'Export failed');
    } finally {
      setIsDetailedExporting(false);
    }
  }, [isDetailedExporting, reportFilters]);

  const handleCallDirectionChange = useCallback(
    (direction: CallDirectionFilter) => {
      const nextSortBy = DefaultSortByField[direction as DialerReportType];
      const nextUserIds = reportFilters?.userIds;
      const nextStart = reportFilters?.startDate;
      const nextEnd = reportFilters?.endDate;
      setCallDirection(direction);
      setSortBy(nextSortBy);
      setReportPagination(() => ({ page: 1, limit: 25 }));
      fetchReportsData({
        callDirection: direction,
        userIds: nextUserIds,
        startDate: nextStart,
        endDate: nextEnd,
        sortBy: nextSortBy,
        sortOrder,
        pageNumber: 1,
        pageSize: 25,
      });
    },
    [
      reportFilters?.endDate,
      reportFilters?.startDate,
      reportFilters?.userIds,
      reportPagination?.limit,
      sortOrder,
      fetchReportsData,
    ],
  );

  const teamData = useMemo(() => getTeamListData(getReportTeamListResponse), [
    getReportTeamListResponse,
  ]);
  const individualData = useMemo(
    () => getIndividualListData(getReportIndividualListResponse),
    [getReportIndividualListResponse],
  );

  const accordionRows = useMemo(
    () =>
      teamData.map((team) => mapTeamDataToAccordionRow(team, callDirection)),
    [teamData, callDirection],
  );

  const individualRows = useMemo(
    () =>
      individualData.map((user) => mapUserCallDataToRow(user, callDirection)),
    [individualData, callDirection],
  );

  const statsPayload = useMemo(() => {
    const resp = getReportsStatsResponse as
      | Record<string, unknown>
      | null
      | undefined;
    if (!resp) return undefined;
    return (resp.payload ?? resp) as Record<
      string,
      { value: number; trend: number; percentage?: string }
    >;
  }, [getReportsStatsResponse]);

  const summaryItems: DialerReportSummaryItem[] = useMemo(
    () => mapReportsStatsToSummaryItems(statsPayload, callDirection),
    [statsPayload, callDirection],
  );

  const tableData =
    reportTableMode === 'individual' ? individualRows : accordionRows;
  const tableVariant = reportTableMode === 'individual' ? 'flat' : 'accordion';

  const sortConfig = useMemo(
    () => ({
      sortBy,
      order: (sortOrder as unknown) as SortOrder,
    }),
    [sortBy, sortOrder],
  );

  const reportListTotalRows = useMemo(() => {
    const response =
      reportTableMode === 'team'
        ? getReportTeamListResponse
        : getReportIndividualListResponse;
    return getReportListTotal(response);
  }, [
    reportTableMode,
    getReportTeamListResponse,
    getReportIndividualListResponse,
  ]);

  const handleReportPaginationChange = useCallback(
    (options: { page?: number; limit?: number; totalElements?: number }) => {
      const requestedPage = options.page ?? reportPagination.page;
      const nextLimit = options.limit ?? reportPagination.limit;
      const totalRows = reportListTotalRows;
      const maxPage =
        totalRows <= 0 ? 1 : Math.max(1, Math.ceil(totalRows / nextLimit));
      const nextPage = Math.min(Math.max(1, requestedPage), maxPage);

      setReportPagination({ page: nextPage, limit: nextLimit });
      fetchReportsData({
        pageNumber: nextPage,
        pageSize: nextLimit,
      });
    },
    [
      reportPagination.page,
      reportPagination.limit,
      reportListTotalRows,
      fetchReportsData,
    ],
  );

  const reportPaginationOptionsForTable = useMemo(
    () => ({
      page: reportPagination.page,
      limit: reportPagination.limit,
      totalElements: reportListTotalRows,
    }),
    [reportPagination.page, reportPagination.limit, reportListTotalRows],
  );

  /** Initial fetch on mount (no-op until userIds exist; host-list effect loads callers). */
  useEffect(() => {
    fetchReportsDataRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, same pattern as call-logs.tsx
  }, []);

  /** Reset Team List Request */
  useEffect(() => {
    executeOnRequestStatus({
      status: getReportTeamListRequestStatus,
      onSuccess: () => {
        resetGetReportTeamListRequest();
      },
      onFailed: () => {
        resetGetReportTeamListRequest();
      },
    });
  }, [getReportTeamListRequestStatus]);

  /** Reset Individual List Request */
  useEffect(() => {
    executeOnRequestStatus({
      status: getReportIndividualListRequestStatus,
      onSuccess: () => {
        resetGetReportIndividualListRequest();
      },
      onFailed: () => {
        resetGetReportIndividualListRequest();
      },
    });
  }, [getReportIndividualListRequestStatus]);

  /** Reset Reports Stats Request */
  useEffect(() => {
    executeOnRequestStatus({
      status: getReportsStatsRequestStatus,
      onSuccess: () => {
        resetGetReportsStatsRequest();
      },
      onFailed: () => {
        resetGetReportsStatsRequest();
      },
    });
  }, [getReportsStatsRequestStatus]);

  /** Reset Export Call Reports Request */
  useEffect(() => {
    executeOnRequestStatus({
      status: exportCallReportsRequestStatus,
      onSuccess: () => {
        resetExportCallReportsRequest();
      },
      onFailed: () => {
        resetExportCallReportsRequest();
      },
    });
  }, [exportCallReportsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: assignCallOutcomeRequestStatus?.status,
      onSuccess: () => {
        toaster.success(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
      onFailed: () => {
        toaster.error(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
    });
  }, [assignCallOutcomeRequestStatus?.status]);

  // reset Note Request Status
  useEffect(() => {
    executeOnRequestStatus({
      status: createCallLogsNoteRequestStatus,
      onSuccess: () => {
        toaster.success('Note saved successfully');
        resetCreateCallLogsNoteRequest();
      },
      onFailed: () => {
        resetCreateCallLogsNoteRequest();
      },
    });
  }, [createCallLogsNoteRequestStatus, resetCreateCallLogsNoteRequest]);

  const renderTable = () => (
    <ReportsIncomingTable
      key={callDirection}
      data={tableData}
      isLoading={isListLoading}
      variant={tableVariant}
      sort={sortConfig}
      onSort={handleSort}
      callDirection={callDirection}
      paginationOptions={reportPaginationOptionsForTable}
      onPaginationOptionsChange={handleReportPaginationChange}
    />
  );

  console.warn(
    '[DEBUG] Reports render - handleDetailedExport:',
    typeof handleDetailedExport,
  );

  return (
    <div className="call-logs-reports">
      <CallLogsHeader
        onMakeCallClick={onMakeCallClick}
        showDialer={showDialer}
        callLogsListCountsResponse={callLogsListCountsResponse}
      />
      <ReportsSubHeader
        onExport={handleExport}
        onDetailedExport={handleDetailedExport}
        callDirection={callDirection}
        onCallDirectionChange={handleCallDirectionChange}
        onFiltersChange={handleFiltersChange}
        selectedUserIds={reportFilters.userIds}
      />
      <div className="call-logs-reports__content">
        <div className="dialer-reports-summary-section">
          <ReportsSummaryCards items={summaryItems} loading={isStatsLoading} />
        </div>
        <div
          className={`dialer-reports-table-section ${
            isListLoading ? 'dialer-reports-table-section--loading' : ''
          }`}
        >
          {renderTable()}
        </div>
      </div>
    </div>
  );
};

export default memo(Reports);
